// FILE: src/components/profile/ProfileShell.jsx
// Header shared by the profile pages: who you are, your enrolment at a
// glance, and links to the four profile sections (details, security,
// display & accessibility, privacy & records). Each section is its own page
// so nothing is crowded and each has a shareable URL.
import React from "react";
import { Avatar, Box, Card, CardContent, Stack, Typography } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { PageHeader, StatusChip } from "../ui";

export const PROFILE_SECTIONS = [
  { label: "Details", path: "/student/profile" },
  { label: "Security", path: "/student/profile/security" },
  { label: "Display & accessibility", path: "/student/profile/display" },
  { label: "Privacy & records", path: "/student/profile/privacy" }
];

function initials(p) {
  return `${(p?.firstName || "")[0] || ""}${(p?.lastName || "")[0] || ""}`.toUpperCase() || "?";
}

export default function ProfileShell({ title, description, children }) {
  const { pathname } = useLocation();
  const profile = useSelector((s) => s.profile.profile);
  const current = PROFILE_SECTIONS.findIndex((s) => s.path === pathname.replace(/\/$/, ""));
  const name = [profile?.firstName, profile?.middleName, profile?.lastName].filter(Boolean).join(" ");
  const days = profile?.daysLeft;

  return (
    <Box>
      <PageHeader title={title} description={description} breadcrumbs={[{ label: "My profile", to: "/student/profile" }, { label: PROFILE_SECTIONS[current]?.label || title }]} />

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2.5} sx={{ alignItems: { xs: "flex-start", sm: "center" } }}>
            <Avatar sx={{ width: 64, height: 64, bgcolor: "primary.main", color: "primary.contrastText", fontSize: "1.5rem", fontWeight: 700 }}>
              {initials(profile)}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography component="p" sx={{ fontSize: "1.25rem", fontWeight: 700 }}>
                {name || "Your profile"}
              </Typography>
              <Typography sx={{ color: "text.secondary", overflowWrap: "anywhere" }}>{profile?.email}</Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1.25, flexWrap: "wrap", rowGap: 1 }}>
                {profile?.accountLocked ? (
                  <StatusChip label="Account locked" tone="error" icon={<LockOutlinedIcon />} />
                ) : (
                  <StatusChip label="Active student" tone="success" icon={<CheckCircleOutlineIcon />} />
                )}
                {days != null && (
                  <StatusChip
                    label={days > 0 ? `${days} ${days === 1 ? "day" : "days"} left in your enrolment` : "Enrolment ended"}
                    tone={days > 30 ? "default" : days > 0 ? "warning" : "error"}
                    icon={<EventOutlinedIcon />}
                  />
                )}
              </Stack>
            </Box>
            {profile?.registrationNumber && (
              <Box sx={{ textAlign: { sm: "right" } }}>
                <Typography sx={{ color: "text.secondary" }}>Registration number</Typography>
                <Typography sx={{ fontWeight: 650, fontVariantNumeric: "tabular-nums" }}>{profile.registrationNumber}</Typography>
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Box component="nav" aria-label="Profile sections" sx={{ borderBottom: 1, borderColor: "divider", mb: 3, overflowX: "auto" }}>
        <Box component="ul" sx={{ display: "flex", gap: 0.5, listStyle: "none", m: 0, p: 0, minWidth: "max-content" }}>
          {PROFILE_SECTIONS.map((s, i) => {
            const active = i === current;
            return (
              <li key={s.path}>
                <Box
                  component={RouterLink}
                  to={s.path}
                  aria-current={active ? "page" : undefined}
                  sx={{
                    display: "block",
                    px: 2,
                    py: 1.5,
                    minHeight: 48,
                    fontWeight: active ? 700 : 600,
                    color: active ? "primary.main" : "text.secondary",
                    textDecoration: "none",
                    borderBottom: "3px solid",
                    borderColor: active ? "primary.main" : "transparent",
                    borderRadius: "8px 8px 0 0",
                    "&:hover": { color: "text.primary", bgcolor: "action.hover" }
                  }}
                >
                  {s.label}
                </Box>
              </li>
            );
          })}
        </Box>
      </Box>

      {children}
    </Box>
  );
}
